import LogoutIcon from "@mui/icons-material/Logout";
import LoopIcon from "@mui/icons-material/Loop";
import {
  Close,
  Info,
  Key,
  PersonAdd,
} from "@mui/icons-material";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
  styled,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import appRoutes from "app/config/routes";
import { resetAuthSlice } from "app/modules/auth/operations";
import { resetSchoolSlice } from "app/modules/schools/operations";
import { clearClassList } from "app/services/classDataSlice";
import {
  schoolListSelector,
  userDetailSelector,
  userNameSelector,
  userPrimaryRoleSelector,
} from "app/modules/schools/selectors";
import { changePasswordWithoutOtp } from "app/services/ledgerAccount";
import { persistor } from "app/store";
import { generateNameInitial } from "app/utils/helper";
import { useState } from "react";
import { useMutation } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { THEME } from "app/config/constants";
import ChangePasswordDialog from "./ChangePasswordDialog";
import InvitationCodeDialog from "./InvitationCodeDialog";
import * as yup from "yup";
import { useFormik } from "formik";

const UserProfileDrawer = ({ setState, state }) => {
  const [passwordShow, setpasswordShow] = useState(false);
  const [invitationCodeShow, setInvitationCodeShow] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const schoolList = useSelector(schoolListSelector);
  const userFullName = useSelector(userNameSelector);
  const userDetail = useSelector(userDetailSelector);
  const primaryRole = useSelector(userPrimaryRoleSelector);

  const validationSchema = yup.object().shape({
    currentPassoword: yup
      .string()
      .required("Current password is required")
      .min(6, "Current password must be at least 6 characters"),
    newPassword: yup
      .string()
      .min(6, "Password must be at least 6 characters")
      .max(50, "Password must not exceed 50 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      )
      .notOneOf(
        [yup.ref("currentPassoword")],
        "New password must be different from current password"
      )
      .required("New password is required"),
    confirmNewPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], "Passwords must match")
      .required("Please confirm your password"),
  });

  const { mutate, isLoading: changePasswordWithoutOtpLoading } = useMutation(
    changePasswordWithoutOtp,
    {
      onSuccess: ({ data }) => {
        toast.success(data?.message || "Password changed successfully!");
        setpasswordShow(false);
        resetForm();
        setShowPasswords({ current: false, new: false, confirm: false });

        // Clear local storage and redirect
        localStorage.removeItem("schoolToken");
        localStorage.removeItem("schoolRefreshToken");
        localStorage.removeItem("authRefreshToken");
        localStorage.removeItem("selectedSchoolId");
        localStorage.removeItem("activeSession");

        // Close drawer
        setState((prev) => ({ ...prev, showSidebar: false }));

        // Redirect to login
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 500);
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
            "Failed to change password. Please try again."
        );
      },
    }
  );

  const { values, handleChange, handleSubmit, resetForm, touched, errors } =
    useFormik({
      initialValues: {
        currentPassoword: "",
        newPassword: "",
        confirmNewPassword: "",
      },
      validationSchema,
      onSubmit: (formValues) => {
        let query = {
          confirmPassword: formValues?.confirmNewPassword,
          currentPassword: formValues?.currentPassoword,
          password: formValues?.newPassword,
        };
        mutate(query);
      },
    });

  const navigateToProfile = () => {
    setState((prev) => ({ ...prev, showSidebar: false }));
    navigate(`/${appRoutes.userProfile}`);
  };

  const handleChangePassword = () => {
    setpasswordShow(true);
  };
  
  const handleInvitationCode = () => {
    setInvitationCodeShow(true);
  };

  const handleLogout = async () => {
    try {
      setState((prev) => ({ ...prev, showSidebar: false }));
      dispatch(clearClassList());
      dispatch(resetAuthSlice());
      dispatch(resetSchoolSlice());

      localStorage.removeItem("schoolToken");
      localStorage.removeItem("schoolRefreshToken");
      localStorage.removeItem("authRefreshToken");
      localStorage.removeItem("selectedSchoolId");

      await persistor.purge();

      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login", { replace: true });
      window.location.reload();
    }
  };

  const handleSwitchSchools = () => {
    localStorage.removeItem("schoolToken");
    localStorage.removeItem("schoolRefreshToken");
    localStorage.removeItem("selectedSchoolId");
    setState((prev) => ({ ...prev, showSidebar: false }));
    navigate("/schools", { replace: true });
  };

  const menuItems = [
    {
      icon: <Key />,
      label: "Change Password",
      onClick: handleChangePassword,
      show: true,
    },
    {
      icon: <LoopIcon />,
      label: "Switch Schools",
      onClick: handleSwitchSchools,
      show: schoolList?.length > 1,
    },
    {
      icon: <LogoutIcon />,
      label: "Logout",
      onClick: handleLogout,
      show: true,
      divider: true,
    },
  ];

  const bottomMenuItems = [
    {
      icon: <PersonAdd />,
      label: "Invitation Code",
      onClick: handleInvitationCode,
      show: true,
    },
    {
      icon: <Info />,
      label: "School Profile",
      onClick: () => {
        navigate(`/${appRoutes.schoolProfile}`);
        setState((prev) => ({ ...prev, showSidebar: false }));
      },
      show: ["ROLE_ADMIN", "ROLE_MANAGING_DIRECTOR", "Admin"].includes(
        primaryRole
      ),
    },
    {
      icon: <Info />,
      label: "About School",
      onClick: () => {
        navigate(`/${appRoutes.aboutSchool}`);
        setState((prev) => ({ ...prev, showSidebar: false }));
      },
      show: true,
    },
  ];

  return (
    <>
      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={passwordShow}
        setpasswordShow={setpasswordShow}
        resetForm={resetForm}
        setShowPasswords={setShowPasswords}
        showPasswords={showPasswords}
        isMobile={isMobile}
        handleSubmit={handleSubmit}
        handleChange={handleChange}
        values={values}
        touched={touched}
        errors={errors}
        changePasswordWithoutOtpLoading={changePasswordWithoutOtpLoading}
      />
      
      {/* Invitation Code Dialog */}
      <InvitationCodeDialog
        open={invitationCodeShow}
        onClose={() => setInvitationCodeShow(false)}
      />

      {/* User Profile Drawer */}
      <Drawer
        anchor="right"
        onClose={() => setState((prev) => ({ ...prev, showSidebar: false }))}
        open={state.showSidebar}
        PaperProps={{
          sx: {
            width: isMobile ? "85%" : "320px",
            maxWidth: "100%",
            backgroundColor: THEME.primary,
            color: THEME.white,
          },
        }}
      >
        {/* Header with Close Button */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2, pb: 1 }}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
            Profile
          </Typography>
          <IconButton
            onClick={() =>
              setState((prev) => ({ ...prev, showSidebar: false }))
            }
            sx={{
              color: THEME.white,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <Close />
          </IconButton>
        </Stack>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />

        {/* User Profile Section */}
        <ProfileSection onClick={navigateToProfile}>
          {userDetail?.profilePic ? (
            <StyledAvatar alt="Profile" src={userDetail?.profilePic} />
          ) : (
            <StyledAvatar>
              {generateNameInitial(userDetail?.firstName, userDetail?.lastName)}
            </StyledAvatar>
          )}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "18px",
              mt: 1.5,
              textAlign: "center",
              wordBreak: "break-word",
              px: 2,
            }}
          >
            {userFullName}
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              opacity: 0.85,
              mt: 0.5,
              textTransform: "capitalize",
            }}
          >
            {primaryRole?.replace("ROLE_", "").replace(/_/g, " ") || "User"}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              opacity: 0.7,
              mt: 0.5,
              cursor: "pointer",
              "&:hover": {
                opacity: 1,
                textDecoration: "underline",
              },
            }}
          >
            View Profile
          </Typography>
        </ProfileSection>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)", my: 2 }} />

        {/* Menu Items */}
        <Stack
          sx={{ flex: 1, height: "100%" }}
          direction="column"
          justifyContent="space-between"
        >
          <Box>
            {menuItems.map(
              (item, index) =>
                item.show && (
                  <Box key={index}>
                    <MenuItem onClick={item.onClick}>
                      <Stack direction="row" alignItems="center" gap={2}>
                        {item.icon}
                        <Typography sx={{ fontSize: "14px" }}>
                          {item.label}
                        </Typography>
                      </Stack>
                    </MenuItem>
                    {item.divider && (
                      <Divider
                        sx={{
                          borderColor: "rgba(255, 255, 255, 0.2)",
                          my: 1,
                        }}
                      />
                    )}
                  </Box>
                )
            )}

            {/* Logout button */}
            {/* <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ cursor: "pointer", px: 3, py: 1 }}
              onClick={async () => {
                try {
                  // Clear state first
                  setState((prev) => ({ ...prev, showSidebar: false }));

                  // Clear Redux slices
                  dispatch(clearClassList());
                  dispatch(resetAuthSlice());
                  dispatch(resetSchoolSlice());

                  // Clear localStorage
                  localStorage.removeItem("schoolToken");
                  localStorage.removeItem("schoolRefreshToken");
                  localStorage.removeItem("authRefreshToken");
                  localStorage.removeItem("authAccessToken");
                  localStorage.removeItem("selectedSchoolId");
                  localStorage.removeItem("activeSession");

                  // Purge persist store
                  await persistor.purge();
                  // await persistor.flush();

                  // Navigate to login
                  navigate("/login", { replace: true });
                } catch (error) {
                  console.error("Logout error:", error);
                  navigate("/login", { replace: true });
                  window.location.reload();
                }
              }}
            >
              <LogoutIcon />
              <Typography sx={{ fontSize: "14px" }}>Logout</Typography>
            </Stack> */}
          </Box>

          {/* Bottom Menu Items */}
          <Box sx={{ pb: 2 }}>
            {bottomMenuItems.map(
              (item, index) =>
                item.show && (
                  <MenuItem key={index} onClick={item.onClick}>
                    <Stack direction="row" alignItems="center" gap={2}>
                      {item.icon}
                      <Typography sx={{ fontSize: "14px" }}>
                        {item.label}
                      </Typography>
                    </Stack>
                  </MenuItem>
                )
            )}
          </Box>
        </Stack>
      </Drawer>
    </>
  );
};

export default UserProfileDrawer;

// Styled Components
const ProfileSection = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 24px 20px 20px;
  transition: all 0.2s ease-in-out;
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.08);
  }
`;

const StyledAvatar = styled(Avatar)`
  height: 80px;
  width: 80px;
  font-size: 32px;
  font-weight: 600;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.25) 0%,
    rgba(255, 255, 255, 0.15) 100%
  );
  border: 3px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  }
`;

const MenuItem = styled(Box)`
  padding: 14px 24px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: rgba(255, 255, 255, 0.5);
    transform: scaleY(0);
    transition: transform 0.2s ease-in-out;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    padding-left: 28px;

    &::before {
      transform: scaleY(1);
    }
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;
